import React, { useCallback, useEffect, useState } from 'react';
import ReactFlow, {
  ReactFlowProvider, Background, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useSnackbar } from 'notistack';
import API from '../../api/API.jsx';
import nodeTypes from './nodes/nodeTypes';
import edgeTypes from './edges/edgeTypes';
import {
  backendToFlow, flowToBackend, sourceKind, targetKind, LINK_TYPE, VALID_LINK_TYPES, tipoFromRfType,
} from './formulaAdapter';
import './FormulaCanvasRF.css';

const getEdition = () => {
  try { return JSON.parse(localStorage.getItem('selectedEdition') || 'null'); }
  catch { return null; }
};

const InnerCanvas = ({ readOnly = false }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  // sortPriority globale della formula: non editabile qui (arriva nel #3),
  // ma va conservato e ri-salvato per non perderlo nel round-trip.
  const [sortPriority, setSortPriority] = useState(null);
  // Idem per i gruppi-fase: si dichiarano sul canvas storico, qui si conservano soltanto.
  const [gruppiFase, setGruppiFase] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const edition = getEdition();
    if (!edition?.id) {
      enqueueSnackbar('Nessuna edizione selezionata', { variant: 'warning' });
      return;
    }
    let active = true;
    API.torneo.getFormula(edition.id).then((res) => {
      if (!active) return;
      if (res.isSuccess && res.data) {
        try {
          const parsed = JSON.parse(res.data);
          const flow = backendToFlow(parsed);
          setNodes(flow.nodes);
          setEdges(flow.edges);
          setSortPriority(flow.sortPriority);
          setGruppiFase(flow.gruppiFase);
        } catch {
          enqueueSnackbar('Formula non valida (JSON malformato)', { variant: 'error' });
        }
      }
    }).catch(() => enqueueSnackbar('Errore nel caricamento della formula', { variant: 'error' }));
    return () => { active = false; };
  }, [enqueueSnackbar, setNodes, setEdges]);

  const nextNumericId = useCallback((items, prefix) =>
    items.reduce((max, it) => {
      const n = parseInt(String(it.id).replace(prefix, ''), 10);
      return Number.isNaN(n) ? max : Math.max(max, n);
    }, 0) + 1,
  []);

  const addNode = useCallback((rfType) => {
    // Calcola l'id dentro l'updater funzionale per evitare race su click rapidi.
    setNodes((prev) => {
      const id = nextNumericId(prev, 'node-');
      const tipo = tipoFromRfType(rfType);
      const data = { originalId: id, name: tipo, tipo, incomingConnectionsOrder: {} };
      if (rfType === 'team') data.idSquadra = null;
      if (rfType === 'classifica') data.sortPriority = null;
      if (rfType === 'dirette') {
        Object.assign(data, {
          expanded: true, width: 300, height: 200, fascia: 1,
          internalMatches: [], internalConnections: [], inputMapping: [], outputMapping: [],
        });
      }
      return prev.concat({ id: `node-${id}`, type: rfType, position: { x: 200, y: 150 }, data });
    });
  }, [nextNumericId, setNodes]);

  const onConnect = useCallback((params) => {
    const fromNode = nodes.find((n) => n.id === params.source);
    const toNode = nodes.find((n) => n.id === params.target);
    if (!fromNode || !toNode) return;
    const linkType = LINK_TYPE(sourceKind(fromNode), targetKind(toNode));
    if (!VALID_LINK_TYPES.has(linkType)) {
      enqueueSnackbar(`Collegamento non valido: ${linkType}`, { variant: 'info' });
      return;
    }
    setEdges((prev) => {
      const id = nextNumericId(prev, 'conn-');
      return addEdge({
        ...params,
        id: `conn-${id}`,
        type: 'formulaEdge',
        data: { originalId: id, linkType, fromPosition: 1, toPosition: null, connectionNumber: linkType === 'SquadraToGirone' ? 0 : 1 },
      }, prev);
    });
  }, [nodes, nextNumericId, setEdges, enqueueSnackbar]);

  const save = useCallback(() => {
    const edition = getEdition();
    if (!edition?.id) {
      enqueueSnackbar('Nessuna edizione selezionata', { variant: 'warning' });
      return;
    }
    const json = JSON.stringify(flowToBackend(nodes, edges, sortPriority, gruppiFase));
    API.torneo.setFormula(edition.id, json).then((res) => {
      enqueueSnackbar(res.isSuccess ? 'Formula salvata' : `Salvataggio fallito: ${res.errorMsg || ''}`,
        { variant: res.isSuccess ? 'success' : 'error' });
    }).catch(() => enqueueSnackbar('Errore di rete nel salvataggio', { variant: 'error' }));
  }, [nodes, edges, sortPriority, gruppiFase, enqueueSnackbar]);

  const importJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const flow = backendToFlow(JSON.parse(ev.target.result));
          setNodes(flow.nodes);
          setEdges(flow.edges);
          setSortPriority(flow.sortPriority);
          setGruppiFase(flow.gruppiFase);
          enqueueSnackbar('Formula importata', { variant: 'success' });
        } catch {
          enqueueSnackbar('File JSON non valido', { variant: 'error' });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, [setNodes, setEdges, enqueueSnackbar]);

  return (
    <div className="fcanvas-root">
      {/* a torneo chiuso la formula è in sola visualizzazione: niente toolbar */}
      {!readOnly && (
        <div className="fcanvas-toolbar">
          <button className="fcanvas-btn" onClick={() => addNode('team')}>+ Squadra</button>
          <button className="fcanvas-btn" onClick={() => addNode('girone')}>+ Girone</button>
          <button className="fcanvas-btn" onClick={() => addNode('dirette')}>+ Eliminazione</button>
          <button className="fcanvas-btn" onClick={() => addNode('classifica')}>+ Classifica</button>
          <span className="sep" />
          <button className="fcanvas-btn fcanvas-btn--primary" onClick={save}>Salva</button>
          <button className="fcanvas-btn" onClick={importJSON}>Importa JSON</button>
        </div>
      )}
      <div className="fcanvas-flow">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={readOnly ? undefined : onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          nodesConnectable={!readOnly}
          deleteKeyCode={readOnly ? null : ['Delete', 'Backspace']}
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
};

const FormulaCanvasRF = ({ readOnly = false }) => (
  <ReactFlowProvider>
    <InnerCanvas readOnly={readOnly} />
  </ReactFlowProvider>
);

export default FormulaCanvasRF;
